import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { DialogService, DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { MessageService, ConfirmationService, PrimeNGConfig } from 'primeng/api';
import { Title } from '@angular/platform-browser';
import moment from 'moment';
import { CommonService } from '../../../../services/common/common.service';
import { CategoriesDataService, CategoriesColumnsService } from '../../../services';
import { Category, CategoriesResult } from '../../../interfaces';
import { DialogFilerComponent } from '../../filer/filer.component';

@Component({
  selector: 'app-category-form',
  templateUrl: './category-form.component.html',
  styleUrls: ['./category-form.component.scss'],
  providers: [MessageService, ConfirmationService, DynamicDialogRef, DynamicDialogConfig],
})
export class CategoryFormComponent implements OnInit, OnDestroy, AfterViewInit {

  public category: Category = {};
  public categoryForm: FormGroup = new FormGroup({});
  public categories: Category[] = [];
  public defaultParameters: {[param: string]: string | string[]} = {offset: '0', limit: '1000'};
  public parameters: {[param: string]: string | string[]} = {offset: '0', limit: '1000'};
  public isLoading: boolean = false;
  public locale: string = (localStorage.language)?localStorage.language:'en';
  public redirect = true;
  public disableSubmit = false;
  public enableFormAlert = true;

  private filerDialog: DynamicDialogRef = new DynamicDialogRef();

  constructor(
    private translate: TranslateService,
    public common: CommonService,
    private router: Router,
    private route: ActivatedRoute,
    private dialogService: DialogService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private dynamicDialogConfig: DynamicDialogConfig,
    private dynamicDialogRef: DynamicDialogRef,
    private primengConfig: PrimeNGConfig,
    private titleService: Title,
    private categoriesDataService: CategoriesDataService,
    public categoriesColumnsService: CategoriesColumnsService,
  ) {
  }

  ngOnInit(): void {
    console.log('ngOnInit', 'CategoryFormComponent');
    this.route.params.subscribe((params: any) => {
      if (typeof params['id'] !== 'undefined') {
        this.titleService.setTitle('Category - Edit');
        this.getCategory(parseInt(params['id'], 10));
      }
    });
    if (this.dynamicDialogConfig.data && typeof this.dynamicDialogConfig.data.category !== 'undefined') {
      this.category = this.dynamicDialogConfig.data.category;
    }
    if (typeof this.category.id === 'undefined' || this.category.id === null) {
      this.category.id = null;
    }
    this.getCategories();
    this.prepareFrom();
  }

  ngOnDestroy(): void {
    console.log('ngOnDestroy', 'CategoryFormComponent');
  }

  ngAfterViewInit(): void {
    console.log('ngAfterViewInit', 'CategoryFormComponent');
  }

  getCategory(id: number): void {
    this.isLoading = true;
    this.categoriesDataService.getCategory(id).subscribe((response: Category|any) => {
      this.isLoading = false;
      if (response !== null) {
        this.category = response;
        this.prepareFrom();
      }
    });
  }

  getCategories(): void {
    if (this.dynamicDialogConfig.data && typeof this.dynamicDialogConfig.data.categories !== 'undefined' && this.dynamicDialogConfig.data.categories.length > 0) {
      this.categories = this.dynamicDialogConfig.data.categories;
      return;
    }
    this.categoriesDataService.getCategories(this.parameters).subscribe((response: CategoriesResult|any) => {
      this.isLoading = false;
      if (response !== null) {
        this.categories = response.results;
      }
    });
  }

  prepareFrom(): void {
    this.categoryForm = new FormGroup({
      id: new FormControl(this.category.id, []),
      name: new FormControl(this.category.name, [Validators.required, ]),
      nameAr: new FormControl(this.category.nameAr, []),
      nameFr: new FormControl(this.category.nameFr, []),
      slug: new FormControl(this.category.slug, [Validators.required, ]),
      slugAr: new FormControl(this.category.slugAr, []),
      slugFr: new FormControl(this.category.slugFr, []),
      picture: new FormControl(this.category.picture, []),
      color: new FormControl(this.category.color, []),
      description: new FormControl(this.category.description, [Validators.required, ]),
      descriptionAr: new FormControl(this.category.descriptionAr, []),
      descriptionFr: new FormControl(this.category.descriptionFr, []),
      ordering: new FormControl(this.category.ordering, []),
      status: new FormControl(this.category.status, [Validators.required, ]),
    });
  }

  list() {
    this.router.navigate(['/categories']);
  }

  showErrors() {
      this.showErrors();
      const invalidFields: string[] = [];
      Object.keys(this.categoryForm.controls).forEach((field: any) => {
        const control = this.categoryForm.get(field);
        if (control !== null) {
          control.markAsTouched({ onlySelf: true });
          if (control.invalid) {
            invalidFields.push(field);
          }
        }
      });
  }

  submitForm(redirect: boolean) {
    this.redirect = redirect;
    if (!this.categoryForm.valid) {
      this.showErrors();
    } else {
      this.saveData(redirect);
    }
  }

  saveData(redirect: boolean) {
    this.category = this.categoryForm.value;
    this.disableSubmit = true;
    console.log('category', this.category);
    if (typeof this.category.id !== 'undefined' && this.category.id !== null) {
      this.add(redirect);
    } else {
      this.update(redirect);
    }
    return false;
  }

  add(redirect: boolean) {
    this.categoriesDataService.updateCategory(this.category).subscribe((data: any) => {
      this.disableSubmit = false;
      this.messageService.add({severity: 'success', summary: this.translate.instant('NOTIFICATION'), detail: this.translate.instant('CATEGORYUPDATED')});
      if (redirect && typeof this.dynamicDialogConfig.data !== 'undefined') {
        this.dynamicDialogRef.close({refresh: true});
      } else if (redirect) {
        this.list();
      }
    }, (error: any) => {
      this.disableSubmit = false;
      this.messageService.add({severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('CATEGORYNOTUPDATED') + ' ' + error.message});
      console.warn(error);
    });
  }

  update(redirect: boolean) {
    this.categoriesDataService.addCategory(this.category).subscribe((data: any) => {
        this.disableSubmit = false;
        this.messageService.add({severity: 'success', summary: this.translate.instant('NOTIFICATION'), detail: this.translate.instant('CATEGORYCREATED')});
        if (redirect && typeof this.dynamicDialogConfig.data !== 'undefined') {
          this.dynamicDialogRef.close({refresh: true});
        } else if (redirect) {
          this.list();
        }
      }, (error: any) => {
        this.disableSubmit = false;
        this.messageService.add({severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('CATEGORYNOTCREATED') + ' ' + error.message});
        console.warn(error);
    });
  }

  setColor(event: any) {
    const control = this.categoryForm.get('color');
    if (control !== null) {
      control.setValue(event.value);
    }
  }

  openFilerDialog(field: string): void {
    let url = '';
    const control = this.categoryForm.get(field);
    if (control !== null) {
      url = control.value;
    }
    this.filerDialog = this.dialogService.open(DialogFilerComponent, {
      header: '', // this.translate.instant('FILEMANAGER'),
      width: '90%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1500,
      data: {
        path: 'categories',
        field: field,
        url: url,
      },
    });
    this.filerDialog.onClose.subscribe((response: any) => {
      if (response && response.selected) {
        const control = this.categoryForm.get(field);
        if (control !== null) {
          control.setValue(response.selected.webPath);
        }
      }
    });
  }

}

@Component({
  selector: 'app-category-form',
  templateUrl: './category-form.component.html',
  styleUrls: ['./category-form.component.scss'],
  providers: [MessageService, ConfirmationService],
})
export class DialogCategoryFormComponent extends CategoryFormComponent {
}

